/**
 * Waiver injection for activation registrations.
 *
 * A show may carry per-activation waiver copy (`AutoShow.waivers`, parsed
 * from `shows.waiver` / `shows.minor_waiver`; the copy is Markdown). For the
 * waiver-required activation sources, every waiver the show offers is
 * rendered on the `waivers` page — the visitor's own activation first — as a
 * survey-core `markdown` question (sanitised Markdown, no value), above the
 * page's single shared signature / agreement block. (`adultwaiver` /
 * `minorwaiver` composites are NOT used here: each of those carries its own
 * signature, and the microsite signs all waivers once.)
 *
 * The copy is injected as a SurveyJS localizable map (`{ en, es, fr }`), so
 * switching the survey locale re-renders the text in place — the model is
 * NOT rebuilt on a language change and answers survive it.
 */

import { hasReadableWaiverBody } from '@/lib/waiverContent';

/** A plain string, a locale map (`{ en, es?, fr? }` as the API types it, or any `{ [lang]: text }`), or nothing. */
export type LocalizedText =
  string | { en?: string; es?: string; fr?: string } | Readonly<Record<string, string | undefined>> | null | undefined;

export interface ActivationWaiverSet {
  [activationKey: string]: { adult: LocalizedText; minor: LocalizedText } | undefined;
}

/**
 * THE invariant of this module (SA-1671): a submission must never carry a signature for a side
 * (adult or minor) whose waiver text the visitor could not read. Every earlier fix leaked
 * because *presence* kept being treated as *readable text* — first presence of a KEY (`{ en: '' }`
 * and `{ en: 42 }` are non-null objects), then presence of a non-empty STRING (`<!-- no terms -->`,
 * `<p></p>` and `&nbsp;` all render to nothing). Either way a question was injected whose body was
 * a bare heading, which flipped that side's `has…WaiverContent` flag on, which showed and required
 * that side's signature over nothing.
 *
 * So readability is decided exactly once, here, from what the copy actually RENDERS to
 * (`hasReadableWaiverBody`, src/lib/waiverContent.ts), and every other consumer derives from it:
 * a locale whose body renders blank is not content, and a side left with no usable locales is
 * ABSENT — nothing for it is injected, counted or signable.
 *
 * Applied PER LOCALE, which is the case that matters in production: a map whose entry for the
 * visitor's own language renders nothing while another language has real terms keeps only the
 * languages that render, so the visitor gets readable terms in some language (via SurveyJS's
 * `default` fallback, see `localizedWaiverMarkdown`) or nothing at all — never a heading with an
 * empty body under it.
 *
 * Returns the text narrowed to its readable parts (shape preserved: a string stays a string, a
 * locale map keeps only its readable locales), or `null` when nothing readable is left.
 */
export function normalizeWaiverText(text: LocalizedText): string | Record<string, string> | null {
  if (typeof text === 'string') return hasReadableWaiverBody(text) ? text : null;
  if (!text || typeof text !== 'object') return null;
  const entries = Object.entries(text).filter((entry): entry is [string, string] => hasReadableWaiverBody(entry[1]));
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

/** `?t=` sources whose registration requires the waivers page. */
export const requiredWaiverShowTypes: readonly string[] = [
  'broncoMountainRegistration',
  'rideDriveRegistration',
  'simulatorRegistration',
  'evRideRegistration',
];

const sourceToWaiverKey: Record<string, string> = {
  broncoMountainRegistration: 'broncoMountain',
  rideDriveRegistration: 'rideDrive',
  simulatorRegistration: 'simulator',
  evRideRegistration: 'evRide',
};

const waiverKeyToSource: Record<string, string> = Object.fromEntries(
  Object.entries(sourceToWaiverKey).map(([source, key]) => [key, source]),
);

/** Prefix every injected waiver question name carries (styling hook + tests). */
export const WAIVER_QUESTION_PREFIX = 'waiver_';

/**
 * Format activation type into a readable title
 * @param activationType The activation type string from URL parameter
 */
export function formatActivationTitle(activationType: string): string {
  const types: Record<string, string> = {
    broncoMountainRegistration: 'Bronco Off-Rodeo Experience',
    rideDriveRegistration: 'Test Drive',
    simulatorRegistration: 'Simulator',
    evRideRegistration: 'EV Ride',
  };

  if (activationType in types) {
    return types[activationType];
  }

  // Format unknown activation types by removing 'Registration' and capitalizing
  return activationType
    .replace('Registration', '')
    .split(/(?=[A-Z])/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The `markdown` question renders no title of its own, and the design puts the
 * waiver's name INSIDE the scroll box as its first line — so the heading is
 * prepended to the Markdown body (h2: the page already renders its title as
 * the h1).
 */
export function withHeading(title: string, body: string): string {
  return `## ${title}\n\n${body}`;
}

/**
 * The `markdown` property is a SurveyJS localizable string, so the copy goes in
 * as `{ [locale]: heading + body }`. A show that authored no English copy gets
 * its first language as SurveyJS's `default` so nothing renders blank.
 *
 * Returns `null` when there is no readable copy to render (see `normalizeWaiverText`) — a
 * heading with nothing under it is not a waiver, and callers MUST treat `null` as "this side
 * has nothing": the function that renders the body is the same one that decides the side
 * exists, so no downstream gate can disagree with what a visitor actually sees.
 */
export function localizedWaiverMarkdown(title: string, text: LocalizedText): string | Record<string, string> | null {
  const readable = normalizeWaiverText(text);
  if (readable === null) return null;
  if (typeof readable === 'string') return withHeading(title, readable);
  const entries = Object.entries(readable);
  // SurveyJS falls back to the `default` key (not to `en`) when the current
  // locale has no text, so English is stored as `default`.
  const map: Record<string, string> = {};
  for (const [locale, body] of entries) map[locale === 'en' ? 'default' : locale] = withHeading(title, body);
  if (!map.default) map.default = map[entries[0][0]];
  return map;
}

export interface InjectedWaivers {
  adult: string[];
  minor: string[];
}

/**
 * The per-side truth the waivers page runs on: what was injected, and therefore which side's
 * signature may be shown, required and submitted.
 */
export interface WaiverGates {
  injected: InjectedWaivers;
  /** Adult waiver body copy is on the page — the shared `signature`/`waiver_agree` pair may show. */
  hasAdultWaiverContent: boolean;
  /** Minor waiver body copy is on the page — `minorSignature` may show (on top of `minorsYesNo`). */
  hasMinorWaiverContent: boolean;
  /** Either side has content — the page shows waivers rather than the "unavailable" fallback. */
  hasWaiverContent: boolean;
}

/** The names the gates are seeded under; the survey JSONs' `visibleIf` expressions read these. */
export const WAIVER_GATE_VALUES = {
  contentAvailable: 'waiverContentAvailable',
  adult: 'hasAdultWaiverContent',
  minor: 'hasMinorWaiverContent',
} as const;

/**
 * Injects the show's waivers into `config` and derives the page's gates from what was ACTUALLY
 * injected — never from the activation type, the raw waiver JSON, or one side standing in for
 * the other. Each side is gated on its own content so that neither signature can be shown or
 * required over the other side's text, or over its own missing text (SA-1671).
 *
 * This lives here, next to the injector, rather than inline in the page, so the gates and the
 * rendering cannot drift apart and so tests exercise the real derivation.
 */
export function resolveWaiverGates(
  config: any,
  waivers: ActivationWaiverSet | null | undefined,
  source: string,
): WaiverGates {
  const injected = injectWaivers(config, waivers, source);
  const hasAdultWaiverContent = injected.adult.length > 0;
  const hasMinorWaiverContent = injected.minor.length > 0;
  return {
    injected,
    hasAdultWaiverContent,
    hasMinorWaiverContent,
    hasWaiverContent: hasAdultWaiverContent || hasMinorWaiverContent,
  };
}

/** Seeds the gates onto a live survey model so the JSONs' `visibleIf` expressions can read them. */
export function applyWaiverGates(survey: { setValue(name: string, value: unknown): void }, gates: WaiverGates): void {
  survey.setValue(WAIVER_GATE_VALUES.contentAvailable, gates.hasWaiverContent);
  survey.setValue(WAIVER_GATE_VALUES.adult, gates.hasAdultWaiverContent);
  survey.setValue(WAIVER_GATE_VALUES.minor, gates.hasMinorWaiverContent);
}

/**
 * Pushes one `markdown` question per available waiver into the survey JSON's
 * `adultWaiversPanel` / `minorWaiversPanel` (the visitor's own activation
 * first, then every other activation the show offers). Mutates `config`;
 * returns the injected question names so callers/tests can check order.
 * Does nothing when the source needs no waivers or the show has none.
 */
export function injectWaivers(
  config: any,
  waivers: ActivationWaiverSet | null | undefined,
  source: string,
): InjectedWaivers {
  const injected: InjectedWaivers = { adult: [], minor: [] };
  if (!waivers || !requiredWaiverShowTypes.includes(source)) return injected;

  const availableWaiverTypes = Object.keys(waivers);
  if (availableWaiverTypes.length === 0) return injected;

  const sourceWaiverKey = sourceToWaiverKey[source];
  const orderedWaiverTypes = [sourceWaiverKey, ...availableWaiverTypes.filter(t => t !== sourceWaiverKey)];

  const waiverPage = (config.pages as any[] | undefined)?.find((page: any) => page.name === 'waivers');
  const waiversPanel = waiverPage?.elements?.[0];
  if (!waiversPanel || !Array.isArray(waiversPanel.elements)) {
    console.error('Waiver page not found in survey config');
    return injected;
  }
  const adultWaiversPanel = waiversPanel.elements.find((el: any) => el.name === 'adultWaiversPanel');
  const minorWaiversPanel = waiversPanel.elements.find((el: any) => el.name === 'minorWaiversPanel');
  if (!adultWaiversPanel?.elements || !minorWaiversPanel?.elements) {
    console.error('Required waiver panels not found in waiver page');
    return injected;
  }

  // Clear existing elements in the panels
  adultWaiversPanel.elements = [];
  minorWaiversPanel.elements = [];

  for (const waiverType of orderedWaiverTypes) {
    const sourceType = waiverKeyToSource[waiverType];
    const waiverData = waivers[waiverType];
    if (!sourceType || !waiverData) continue;

    const formattedTitle = formatActivationTitle(sourceType);

    // Each side is injected ONLY when the renderer produced readable body copy for it, and the
    // returned name list is what the page derives `hasAdultWaiverContent` / `hasMinorWaiverContent`
    // (and therefore signability) from — so a side with an empty, malformed or non-string entry is
    // simply not here, on either axis. Never re-derive presence from the raw value.
    const adultTitle = `${formattedTitle} Waiver Agreement`;
    const adultMarkdown = localizedWaiverMarkdown(adultTitle, waiverData.adult);
    if (adultMarkdown !== null) {
      const name = `${WAIVER_QUESTION_PREFIX}adult_${sourceType}`;
      adultWaiversPanel.elements.push({
        type: 'markdown',
        name,
        title: adultTitle,
        markdown: adultMarkdown,
      });
      injected.adult.push(name);
    }

    const minorTitle = `${formattedTitle} Minor Participant Agreement`;
    const minorMarkdown = localizedWaiverMarkdown(minorTitle, waiverData.minor);
    if (minorMarkdown !== null) {
      const name = `${WAIVER_QUESTION_PREFIX}minor_${sourceType}`;
      minorWaiversPanel.elements.push({
        type: 'markdown',
        name,
        title: minorTitle,
        markdown: minorMarkdown,
        visibleIf: "{minorsYesNo} = '1'",
      });
      injected.minor.push(name);
    }
  }

  return injected;
}
